<script setup lang="ts">
/**
 * 產品頁〈產品參數版本〉頁籤（批151 C2）。
 *
 * 有些產品要在**不重簽授權**的情況下，由授權中心統一調整一組數值參數，而且要能預約生效。
 * 這一頁管兩件事：
 *
 * 1. **參數宣告**（`products.options.versioned_params`）——有哪些參數鍵、叫什麼、單位。
 *    新增鍵時若已有目前生效／未來的版本，後端要求同時給新鍵的**初始值**（422
 *    `initial_values_required`，帶 `missing_keys`），並在同一筆交易自動補一版立即生效的新版本
 *    （主人裁示 121：可下發的版本永遠完整）。這裡的作法：先照常送，被要求時在**同一個視窗**
 *    展開初始值欄位再送一次。
 * 2. **版本**——每一版涵蓋全部參數鍵、有生效時間；已生效的不可改不可刪，只能再加新版
 *    （裁示 62）；版本編號由授權中心產生、永不重用（裁示 122）。
 *
 * - 讀＝admin 以上；**寫＝營運主管以上**（`canEdit`；後端 `require_manager`）。
 * - 時間一律顯示與輸入**台北時間**（`lib/productParams.ts`）。
 * - 文案中性：不寫任何產品名或參數鍵的字面。
 */
import { computed, nextTick, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import type { ProductSpec } from '@/api/ls'
import * as pp from '@/api/productParams'
import type { ParamVersion, ParamVersionList } from '@/api/productParams'
import { DECIMAL_HINT, canonicalDecimal } from '@/lib/decimalStr'
import {
  HINT_MAX,
  LABEL_MAX,
  MAX_KEYS,
  NOTE_MAX,
  STATE_LABELS,
  UNIT_MAX,
  backfillSummary,
  checkValues,
  declarationFromRows,
  declIssueHits,
  defaultEffectiveInput,
  effectiveError,
  fmtTaipei,
  futureCount,
  keyHeading,
  minEffectiveInput,
  missingInitialKeys,
  paramErrorField,
  paramErrorMessage,
  rowsFromDeclaration,
  seedValues,
  sentIndex,
  taipeiInputFromIso,
  validateDeclarationRows,
  versionBody,
} from '@/lib/productParams'
import type { DeclRow, ParamIssue } from '@/lib/productParams'
import { ROLE_LABELS } from '@/lib/roles'
import { useDialogsStore } from '@/stores/dialogs'
import { vCardTable } from '@/lib/cardTable'

const props = defineProps<{
  product: ProductSpec
  /** 營運主管以上才可編輯（其餘唯讀）。 */
  canEdit: boolean
}>()
/** 宣告存在 `products.options` 裡——寫入後父層要重載產品清單。 */
const emit = defineEmits<{ saved: [] }>()

const dialogs = useDialogsStore()

const STATE_PILL: Record<string, string> = { current: 'active', future: 'pending', past: 'superseded' }

function detailOf(e: unknown): unknown {
  return e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
}

// ---------------------------------------------------------------------------
// 讀取
// ---------------------------------------------------------------------------
const data = ref<ParamVersionList | null>(null)
const loading = ref(false)
const loadError = ref('')

async function load() {
  const id = props.product.id
  loading.value = true
  try {
    const res = await pp.listParamVersions(id)
    if (id === props.product.id) {
      data.value = res
      loadError.value = ''
    }
  } catch (e) {
    loadError.value = paramErrorMessage(detailOf(e), '讀取產品參數版本失敗')
  } finally {
    loading.value = false
  }
}
// 只看 id：存檔後父層重載會換掉 product 物件，但同一個產品不必再讀一次（存檔流程自己會 load）。
watch(
  () => props.product.id,
  () => {
    data.value = null
    void load()
  },
  { immediate: true },
)

const decl = computed(() => data.value?.declaration ?? null)
const keys = computed(() => decl.value?.keys ?? [])
const versions = computed(() => data.value?.items ?? [])
const maxFuture = computed(() => data.value?.max_future_versions ?? 8)
const nFuture = computed(() => futureCount(versions.value))
const addBlockedReason = computed(() => {
  if (!decl.value) return '請先設定參數宣告'
  if (!keys.value.length) return '參數宣告裡還沒有任何參數鍵'
  if (nFuture.value >= maxFuture.value) return `尚未生效的版本最多 ${maxFuture.value} 版`
  return ''
})

// ---------------------------------------------------------------------------
// 參數宣告（彈窗）
// ---------------------------------------------------------------------------
const declOpen = ref(false)
const declRows = ref<DeclRow[]>([])
/** 後端要求初始值的鍵（`initial_values_required` 的 `missing_keys`）；空＝不需要。 */
const declNeed = ref<string[]>([])
const declInitial = ref<Record<string, string>>({})
const declShowErrors = ref(false)
/** 後端點名的欄位錯誤（改了表單就清掉）。 */
const declServerIssue = ref<ParamIssue | null>(null)
/** 不屬於任何一格的錯誤（409、權限…）。 */
const declServerError = ref('')
const declSaving = ref(false)
const declRemoving = ref(false)

function openDecl() {
  declRows.value = rowsFromDeclaration(decl.value)
  if (!declRows.value.length) declRows.value.push({ key: '', label: '', unit: '', hint: '', isNew: true })
  declNeed.value = []
  declInitial.value = {}
  declShowErrors.value = false
  declServerIssue.value = null
  declServerError.value = ''
  declOpen.value = true
}
function addDeclRow() {
  if (declRows.value.length >= MAX_KEYS) return
  declRows.value.push({ key: '', label: '', unit: '', hint: '', isNew: true })
}
function removeDeclRow(i: number) {
  declRows.value.splice(i, 1)
}
watch(
  declRows,
  () => {
    declServerIssue.value = null
    declServerError.value = ''
  },
  { deep: true },
)

const declLive = computed(() => validateDeclarationRows(declRows.value))
const declIssue = computed<ParamIssue | null>(
  () => declServerIssue.value ?? (declShowErrors.value ? declLive.value : null),
)
function declHit(i: number, cell: 'key' | 'label' | 'unit' | 'hint'): boolean {
  const n = sentIndex(declRows.value, i)
  return n >= 0 && declIssueHits(declIssue.value, n, cell)
}
/** 需要初始值、而且仍在這份宣告裡的鍵（使用者可能在被要求之後又刪掉那一列）。 */
const needKeys = computed(() => {
  const present = new Map(declarationFromRows(declRows.value).keys.map((k) => [k.key, k]))
  return declNeed.value.filter((k) => present.has(k)).map((k) => present.get(k)!)
})
const initialErrors = computed<Record<string, string>>(() => {
  const out: Record<string, string> = {}
  for (const k of needKeys.value) {
    const raw = (declInitial.value[k.key] ?? '').trim()
    if (!raw) out[k.key] = '必填'
    else if (canonicalDecimal(raw) == null) out[k.key] = DECIMAL_HINT
  }
  if (declServerIssue.value?.field.startsWith('initial_values.')) {
    out[declServerIssue.value.field.slice('initial_values.'.length)] = declServerIssue.value.message
  }
  return out
})

const declIssueEl = ref<HTMLElement | null>(null)
/** 初始值區塊（後端要求初始值時捲到這裡）。 */
const initEl = ref<HTMLElement | null>(null)
async function revealDeclIssue() {
  await nextTick()
  declIssueEl.value?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

async function saveDecl() {
  declShowErrors.value = true
  if (declLive.value) {
    await revealDeclIssue()
    return
  }
  if (Object.keys(initialErrors.value).length) return
  const body = declarationFromRows(declRows.value)
  const initial: Record<string, string> = {}
  for (const k of needKeys.value) initial[k.key] = canonicalDecimal((declInitial.value[k.key] ?? '').trim()) ?? ''
  declSaving.value = true
  try {
    const res = await pp.putParamDeclaration(props.product.id, body, initial)
    declOpen.value = false
    await load()
    emit('saved')
    await dialogs.notify({ title: '已儲存參數宣告', message: backfillSummary(res?.backfill) })
  } catch (e) {
    const d = detailOf(e)
    const missing = missingInitialKeys(d)
    if (missing) {
      // 裁示 121：新增的鍵要初始值——在同一個視窗展開初始值欄位，讓操作者填完再送一次。
      declNeed.value = missing
      for (const k of missing) if (!(k in declInitial.value)) declInitial.value[k] = ''
      declServerError.value = ''
      declShowErrors.value = false
      await nextTick()
      initEl.value?.scrollIntoView({ block: 'center', behavior: 'smooth' })
      return
    }
    const field = paramErrorField(d)
    if (e instanceof ApiError && e.status === 422 && field) {
      declServerIssue.value = { field, message: paramErrorMessage(d, '格式不正確') }
      await revealDeclIssue()
      return
    }
    declServerError.value = paramErrorMessage(d, '儲存參數宣告失敗')
    await revealDeclIssue()
  } finally {
    declSaving.value = false
  }
}

async function removeDecl() {
  const ok = await dialogs.confirm({
    title: '移除參數宣告',
    message:
      `移除「${props.product.name}」的參數宣告？\n` +
      '移除之後站台的心跳與平台 API 都不再下發參數版本（站台保留最後收到的那一份）。' +
      '既有版本仍保留在授權中心，重新宣告同樣的參數鍵即恢復下發。',
    danger: true,
    confirmText: '移除',
  })
  if (!ok) return
  declRemoving.value = true
  try {
    await pp.deleteParamDeclaration(props.product.id)
    declOpen.value = false
    await load()
    emit('saved')
  } catch (e) {
    await dialogs.notify({ title: '移除失敗', message: paramErrorMessage(detailOf(e), '移除參數宣告失敗') })
  } finally {
    declRemoving.value = false
  }
}

// ---------------------------------------------------------------------------
// 版本（新增／修改彈窗、刪除）
// ---------------------------------------------------------------------------
const verOpen = ref(false)
/** null＝新增；否則＝正在修改的那一版（只有未生效的才進得來）。 */
const verEditing = ref<ParamVersion | null>(null)
/**
 * 「立即生效」（實作期裁示；**只在新增時出現**）：不送生效時間，由伺服器取現在時刻
 * （同一秒已有版本就往後挪一秒）。勾了就隱藏時間欄。
 */
const verImmediate = ref(false)
const verEffective = ref('')
const verValues = ref<Record<string, string>>({})
const verNote = ref('')
const verShowErrors = ref(false)
/** 開窗當下的時間（「不可早於現在」的預檢與輸入框的 min 都以它為準）。 */
const verNow = ref(Date.now())
const verServerField = ref('')
const verServerError = ref('')
const verSaving = ref(false)

function openNewVersion() {
  if (!decl.value || addBlockedReason.value) return
  verEditing.value = null
  verImmediate.value = false
  verNow.value = Date.now()
  verEffective.value = defaultEffectiveInput(verNow.value)
  verValues.value = seedValues(decl.value, versions.value)
  verNote.value = ''
  verShowErrors.value = false
  verServerField.value = ''
  verServerError.value = ''
  verOpen.value = true
}
function openEditVersion(v: ParamVersion) {
  if (!decl.value || !v.editable) return
  verEditing.value = v
  verImmediate.value = false
  verNow.value = Date.now()
  // 保留原本的秒（批151 R3-16）：截到分的話，沒改時間直接儲存也會把生效時間改早。
  verEffective.value = taipeiInputFromIso(v.effective_from)
  verValues.value = Object.fromEntries(keys.value.map((k) => [k.key, v.values[k.key] ?? '']))
  verNote.value = v.note ?? ''
  verShowErrors.value = false
  verServerField.value = ''
  verServerError.value = ''
  verOpen.value = true
}
watch([verEffective, verValues, verNote, verImmediate], () => {
  verServerField.value = ''
  verServerError.value = ''
}, { deep: true })

const verCheck = computed(() =>
  decl.value ? checkValues(decl.value, verValues.value) : { errors: {}, clean: {} },
)
const verEffError = computed(() => (verImmediate.value ? null : effectiveError(verEffective.value, verNow.value)))
const verNoteError = computed(() => ([...verNote.value.trim()].length > NOTE_MAX ? `備註最長 ${NOTE_MAX} 字` : ''))
function verErr(key: string): string {
  if (verServerField.value === key && verServerError.value) return verServerError.value
  return verShowErrors.value ? (verCheck.value.errors[key] ?? '') : ''
}
const verEffShown = computed(() =>
  verServerField.value === 'effective_from' ? verServerError.value : verShowErrors.value ? (verEffError.value ?? '') : '',
)
/** 不屬於任何一格的伺服器錯誤（顯示在視窗頂端）。 */
const verTopError = computed(() =>
  verServerError.value && !['effective_from', 'note', ...keys.value.map((k) => k.key)].includes(verServerField.value)
    ? verServerError.value
    : '',
)

async function saveVersion() {
  verShowErrors.value = true
  if (verEffError.value || Object.keys(verCheck.value.errors).length || verNoteError.value) return
  const body = versionBody({
    // 立即生效只用於新增（修改未生效版本時這個勾選不會出現）。
    immediate: verImmediate.value && !verEditing.value,
    effective: verEffective.value,
    values: verCheck.value.clean,
    note: verNote.value,
  })
  if (!body) return
  verSaving.value = true
  try {
    const editing = verEditing.value
    if (editing) await pp.updateParamVersion(props.product.id, editing.version_id, body)
    else await pp.createParamVersion(props.product.id, body)
    verOpen.value = false
    await load()
  } catch (e) {
    const d = detailOf(e)
    verServerField.value = paramErrorField(d)
    verServerError.value = paramErrorMessage(d, '儲存版本失敗')
  } finally {
    verSaving.value = false
  }
}

async function removeVersion(v: ParamVersion) {
  const ok = await dialogs.confirm({
    title: '刪除版本',
    message: `刪除尚未生效的版本 ${v.version_id}（${fmtTaipei(v.effective_from)} 生效）？\n版本編號不會再被使用；刪除後站台下一次心跳就不會再收到這一版。`,
    danger: true,
    confirmText: '刪除',
  })
  if (!ok) return
  try {
    await pp.deleteParamVersion(props.product.id, v.version_id)
    await load()
  } catch (e) {
    await dialogs.notify({ title: '刪除失敗', message: paramErrorMessage(detailOf(e), '刪除版本失敗') })
    await load()
  }
}
</script>

<template>
  <div class="pp">
    <p class="subtle intro">
      由授權中心統一調整、可以預約生效的一組數值參數。站台在心跳時收到<strong>目前生效的一版＋全部未來版本</strong>，
      到點由站台自己切換；改參數不必重簽授權。參數鍵與意思由這個產品自己定義。
    </p>

    <div v-if="loading && !data" class="loading-wrap"><span class="spinner" /></div>
    <p v-else-if="loadError" class="pp-alert">{{ loadError }}</p>

    <template v-else-if="data">
      <!-- ① 參數宣告 -->
      <div class="card-head sub">
        <h3 class="section-title">參數宣告</h3>
        <span class="spacer" />
        <UiButton v-if="canEdit" size="sm" variant="secondary" @click="openDecl">
          {{ decl ? '編輯宣告' : '設定參數宣告' }}
        </UiButton>
      </div>
      <div v-if="decl && keys.length" class="tbl-scroll" data-hscroll>
        <table v-card-table class="tbl card-table">
          <thead>
            <tr><th>代號</th><th>名稱</th><th>單位</th><th>說明</th></tr>
          </thead>
          <tbody>
            <tr v-for="k in keys" :key="k.key">
              <td><code class="mono">{{ k.key }}</code></td>
              <td>{{ k.label }}</td>
              <td class="subtle">{{ k.unit ?? '—' }}</td>
              <td class="subtle">{{ k.hint ?? '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty">
        <div class="empty-title">{{ decl ? '參數宣告裡還沒有任何參數鍵' : '這個產品還沒有參數宣告' }}</div>
        <div class="empty-sub">
          先宣告有哪些參數（代號、名稱、單位），才能建立版本。
          <template v-if="!canEdit">只有{{ ROLE_LABELS.manager }}以上能設定。</template>
        </div>
      </div>

      <!-- ② 版本 -->
      <template v-if="decl">
        <div class="card-head sub">
          <h3 class="section-title">版本</h3>
          <span class="count">{{ data.total }}</span>
          <span class="spacer" />
          <small v-if="canEdit && addBlockedReason" class="subtle">{{ addBlockedReason }}</small>
          <UiButton
            v-if="canEdit"
            size="sm"
            variant="secondary"
            :disabled="!!addBlockedReason"
            @click="openNewVersion"
          >
            ＋ 新增版本
          </UiButton>
        </div>
        <div class="tbl-scroll" data-hscroll>
          <table v-card-table class="tbl card-table">
            <thead>
              <tr>
                <th>版本</th>
                <th>生效時間（台北）</th>
                <th v-for="k in keys" :key="k.key" class="num key-th" :title="k.hint ?? k.key">
                  {{ k.label }}<small v-if="k.unit" class="unit">{{ k.unit }}</small>
                </th>
                <th>備註</th>
                <th>狀態</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="v in versions" :key="v.version_id" :class="{ past: v.state === 'past' }">
                <td><code class="mono">{{ v.version_id }}</code></td>
                <td class="nowrap">{{ fmtTaipei(v.effective_from) }}</td>
                <td v-for="k in keys" :key="k.key" class="num mono">{{ v.values[k.key] ?? '—' }}</td>
                <td class="subtle note-cell" :title="v.note ?? ''">{{ v.note ?? '' }}</td>
                <td class="nowrap">
                  <span class="status-pill" :class="STATE_PILL[v.state]">{{ STATE_LABELS[v.state] }}</span>
                  <span
                    v-if="v.missing_keys.length && v.state !== 'past'"
                    class="chip over"
                    :title="`缺少參數：${v.missing_keys.join('、')}——這一版不會下發`"
                  >缺參數</span>
                </td>
                <td class="acts-cell">
                  <div v-if="canEdit && v.editable" class="acts">
                    <button type="button" class="x" title="修改" @click="openEditVersion(v)">✎</button>
                    <button type="button" class="x danger" title="刪除" @click="removeVersion(v)">✕</button>
                  </div>
                </td>
              </tr>
              <tr v-if="!versions.length">
                <td :colspan="5 + keys.length" class="subtle empty-row">
                  還沒有任何版本——站台目前收不到參數。
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="subtle">
          已生效的版本不可修改或刪除，要調整請新增一版（可勾「立即生效」）；生效時間不可早於現在、同一時間點不可有兩版；
          尚未生效的版本最多 {{ maxFuture }} 版。版本編號由系統產生、刪除後也不會再被使用。
        </p>
      </template>
    </template>

    <!-- 參數宣告彈窗 -->
    <UiModal
      :open="declOpen"
      :title="decl ? '編輯參數宣告' : '設定參數宣告'"
      width="860px"
      :close-on-backdrop="false"
      @close="declOpen = false"
    >
      <div ref="declIssueEl">
        <p v-if="declServerError" class="pp-alert">{{ declServerError }}</p>
        <p v-else-if="declIssue" class="pp-alert">
          {{ declIssue.field ? `欄位 ${declIssue.field}：` : '' }}{{ declIssue.message }}
        </p>
      </div>
      <p class="subtle">
        代號建立後不可更改（站台以它對應參數）；要換代號請刪除再新增。刪除參數鍵之後，既有版本裡的那個值就不再下發。
      </p>
      <div class="decl-rows">
        <div class="decl-row decl-head" aria-hidden="true">
          <span>代號</span><span>名稱</span><span>單位</span><span>說明</span><span />
        </div>
        <div v-for="(r, i) in declRows" :key="i" class="decl-row">
          <input
            v-model="r.key"
            class="mono"
            :class="{ invalid: declHit(i, 'key') }"
            :disabled="!r.isNew"
            placeholder="例：param_a"
            maxlength="40"
            aria-label="代號"
          />
          <input
            v-model="r.label"
            :class="{ invalid: declHit(i, 'label') }"
            placeholder="例：換算率"
            :maxlength="LABEL_MAX"
            aria-label="名稱"
          />
          <input
            v-model="r.unit"
            :class="{ invalid: declHit(i, 'unit') }"
            placeholder="選填，例：元／件"
            :maxlength="UNIT_MAX"
            aria-label="單位"
          />
          <input
            v-model="r.hint"
            :class="{ invalid: declHit(i, 'hint') }"
            placeholder="選填"
            :maxlength="HINT_MAX"
            aria-label="說明"
          />
          <button type="button" class="x danger" title="刪除這個參數鍵" @click="removeDeclRow(i)">✕</button>
        </div>
      </div>
      <div class="decl-add">
        <UiButton size="sm" variant="secondary" :disabled="declRows.length >= MAX_KEYS" @click="addDeclRow">
          ＋ 新增參數鍵
        </UiButton>
        <small class="subtle">代號：小寫英數與底線、字母開頭、最長 40 字；最多 {{ MAX_KEYS }} 個。</small>
      </div>

      <!-- 裁示 121：新增的鍵要初始值（後端要求時才展開） -->
      <template v-if="needKeys.length">
        <h4 ref="initEl" class="sub-title">新參數鍵的初始值</h4>
        <p class="warn-text">
          目前生效（或尚未生效）的版本沒有這些參數。儲存時系統會以「目前生效那一版的值＋這裡的初始值」
          自動補一版<strong>立即生效</strong>的新版本，並把尚未生效的版本一併補上。
        </p>
        <div class="init-grid">
          <label v-for="k in needKeys" :key="k.key" class="field">
            <span class="field-label">{{ keyHeading(k) }} <code class="mono">{{ k.key }}</code></span>
            <input
              v-model="declInitial[k.key]"
              class="mono num-in"
              :class="{ invalid: declShowErrors && initialErrors[k.key] }"
              inputmode="decimal"
              placeholder="例：10"
            />
            <small v-if="declShowErrors && initialErrors[k.key]" class="err">{{ initialErrors[k.key] }}</small>
          </label>
        </div>
      </template>

      <template #foot>
        <UiButton
          v-if="decl"
          class="del-btn"
          :loading="declRemoving"
          @click="removeDecl"
        >
          移除宣告…
        </UiButton>
        <UiButton @click="declOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="declSaving" @click="saveDecl">
          {{ needKeys.length ? '補上初始值並儲存' : '儲存宣告' }}
        </UiButton>
      </template>
    </UiModal>

    <!-- 版本彈窗 -->
    <UiModal
      :open="verOpen"
      :title="verEditing ? `修改版本 ${verEditing.version_id}` : '新增版本'"
      width="640px"
      :close-on-backdrop="false"
      @close="verOpen = false"
    >
      <p v-if="verTopError" class="pp-alert">{{ verTopError }}</p>
      <!-- 實作期裁示：立即生效（只在新增時出現）。勾選框放在時間欄上方，切換時它自己不會移動。 -->
      <label v-if="!verEditing" class="chk imm">
        <input v-model="verImmediate" type="checkbox" />
        立即生效
        <small class="subtle">（以授權中心收到的時間為準；存檔後這一版就不能再修改或刪除）</small>
      </label>
      <p v-if="verImmediate && !verEditing" class="warn-text imm-note">
        這一版存檔後馬上成為目前生效的版本，站台下一次心跳就會收到；要預約生效請取消勾選並選擇時間。
      </p>
      <label v-else class="field">
        <span class="field-label">生效時間（台北時間）</span>
        <!-- step=1：顯示並保留秒（修改帶秒的未生效版本時，原本的秒不能被截掉）。 -->
        <input
          v-model="verEffective"
          type="datetime-local"
          step="1"
          class="eff-in"
          :class="{ invalid: !!verEffShown }"
          :min="minEffectiveInput(verNow)"
        />
        <small v-if="verEffShown" class="err">{{ verEffShown }}</small>
        <small v-else class="subtle">不可早於現在；到點後這一版就不能再修改或刪除。</small>
      </label>
      <div class="ver-grid">
        <label v-for="k in keys" :key="k.key" class="field">
          <span class="field-label" :title="k.hint ?? k.key">{{ keyHeading(k) }}</span>
          <input
            v-model="verValues[k.key]"
            class="mono num-in"
            :class="{ invalid: !!verErr(k.key) }"
            inputmode="decimal"
          />
          <small v-if="verErr(k.key)" class="err">{{ verErr(k.key) }}</small>
          <small v-else-if="k.hint" class="subtle">{{ k.hint }}</small>
        </label>
      </div>
      <p class="subtle">
        每一版都要填滿全部參數；數值是非負的十進位數字（整數最多 12 位、小數最多 6 位）。
        <template v-if="!verEditing">預設帶入上一版的值。</template>
      </p>
      <label class="field">
        <span class="field-label">備註（選填）</span>
        <input
          v-model="verNote"
          :class="{ invalid: (verShowErrors && !!verNoteError) || verServerField === 'note' }"
          :maxlength="NOTE_MAX"
          placeholder="例：依公告調整"
        />
        <small v-if="verServerField === 'note'" class="err">{{ verServerError }}</small>
        <small v-else-if="verShowErrors && verNoteError" class="err">{{ verNoteError }}</small>
      </label>
      <template #foot>
        <UiButton @click="verOpen = false">取消</UiButton>
        <UiButton variant="primary" :loading="verSaving" @click="saveVersion">
          {{ verEditing ? '儲存' : '新增' }}
        </UiButton>
      </template>
    </UiModal>
  </div>
</template>

<style scoped>
.pp {
  display: grid;
  gap: var(--sp-2);
  margin-top: var(--sp-3);
}
.intro {
  margin: 0;
}
.card-head.sub {
  margin-top: var(--sp-3);
  gap: var(--sp-2);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.pp-alert {
  margin: 0 0 var(--sp-2);
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(209, 67, 67, 0.45);
  border-radius: var(--r-sm);
  background: rgba(209, 67, 67, 0.08);
  color: var(--danger);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: var(--sp-5) 0;
}
.nowrap {
  white-space: nowrap;
}
.mono {
  font-family: var(--font-mono);
}
.tbl th.num,
.tbl td.num {
  text-align: right;
  white-space: nowrap;
}
/* 參數欄頭：名稱一行、單位一行（鍵一多就靠表格橫向捲動，不硬擠）。 */
.tbl thead th.key-th {
  white-space: normal;
  min-width: 72px;
  line-height: 1.35;
}
.key-th .unit {
  display: block;
  font-weight: 400;
  color: var(--fg-4);
}
.tbl tr.past td {
  color: var(--fg-3);
}
.note-cell {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.empty-row {
  text-align: center;
  padding: var(--sp-5) 0;
}
.chip.over {
  margin-left: 4px;
}
/* 表格內的小圖示按鈕（與 ProductsView 的 `.x` 同一套） */
.x {
  border: none;
  background: none;
  color: var(--fg-4);
  cursor: pointer;
  font-size: var(--fs-13);
  padding: 2px 4px;
  border-radius: var(--r-sm);
}
.x:hover {
  background: var(--bg-hover);
  color: var(--fg);
}
.x.danger:hover {
  color: var(--danger);
}
/* --- 宣告彈窗 --- */
.decl-rows {
  display: grid;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
}
.decl-row {
  display: grid;
  grid-template-columns: minmax(130px, 1fr) minmax(130px, 1fr) minmax(100px, 0.8fr) minmax(140px, 1.4fr) 24px;
  gap: var(--sp-2);
  align-items: center;
}
.decl-row > input {
  min-width: 0;
}
.decl-head {
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.decl-add {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-top: var(--sp-2);
  flex-wrap: wrap;
}
.sub-title {
  margin: var(--sp-4) 0 var(--sp-2);
  font-size: var(--fs-13);
  color: var(--fg-3);
  font-weight: 600;
}
.warn-text {
  margin: 0 0 var(--sp-2);
  color: var(--warning);
  font-size: var(--fs-12);
  line-height: var(--lh-base);
}
.init-grid,
.ver-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--sp-3);
  margin-top: var(--sp-2);
}
.ver-grid {
  margin: var(--sp-3) 0 var(--sp-2);
}
.eff-in {
  max-width: 240px;
}
.chk {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  color: var(--fg-2);
  cursor: pointer;
  user-select: none;
}
.chk.imm {
  margin-bottom: var(--sp-3);
}
.imm-note {
  margin: 0;
}
.err {
  color: var(--danger);
  font-size: var(--fs-12);
}
input.invalid {
  border-color: var(--danger) !important;
}
/* 移除宣告放在頁尾最左邊（與右邊的儲存隔開，不會按錯；同 ProductsView 的刪除產品）。 */
.del-btn {
  margin-right: auto;
}
/* 手機（批171）：宣告列的四欄放不下（單位、說明被切出右緣）→ 每列兩行：
   「代號／名稱／刪除」一行、「單位／說明」一行；表頭用同一套擺位，兩行各自對得上。 */
@media (--mobile) {
  .decl-row {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 24px;
    row-gap: 4px;
  }
  .decl-row > :nth-child(1) {
    grid-area: 1 / 1;
  }
  .decl-row > :nth-child(2) {
    grid-area: 1 / 2;
  }
  .decl-row > :nth-child(3) {
    grid-area: 2 / 1;
  }
  .decl-row > :nth-child(4) {
    grid-area: 2 / 2 / 3 / 4;
  }
  .decl-row > :nth-child(5) {
    grid-area: 1 / 3;
  }
  .decl-rows > .decl-row + .decl-row:not(.decl-head) {
    padding-top: var(--sp-2);
    border-top: 1px solid var(--border-2);
  }
  .init-grid,
  .ver-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
